import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// ---------------------------------------------------------------------------
// Expense categories API client (CLE-140).
//
// Mirrors backend/src/routes/expenseCategories.ts:
//   GET    /api/expense-categories
//   POST   /api/expense-categories
//   PATCH  /api/expense-categories/:id
//   DELETE /api/expense-categories/:id
//   POST   /api/expense-categories/bulk
//
// Categories are per firm: companyId null = shared by every firm. Passing a
// companyId lists the shared categories plus that firm's.
// ---------------------------------------------------------------------------

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export type CategoryKind = 'INCOME' | 'EXPENSE';

export interface ExpenseCategory {
  id: string;
  code: string;
  name: string;
  kind: CategoryKind;
  /** Owning firm; null = shared by every firm. */
  companyId: string | null;
  description: string | null;
  color: string | null;
  isSystem: boolean;
  isActive: boolean;
  sortOrder: number;
}

export interface ExpenseCategoryInput {
  code?: string;
  name: string;
  kind?: CategoryKind;
  /** Only read on create. */
  companyId?: string | null;
  description?: string | null;
  color?: string | null;
  isActive?: boolean;
  sortOrder?: number;
}

export async function listExpenseCategories(
  includeInactive = false,
  companyId?: string | null,
): Promise<ExpenseCategory[]> {
  const params = new URLSearchParams();
  if (includeInactive) params.set('includeInactive', 'true');
  if (companyId) params.set('companyId', companyId);
  const qs = params.toString();
  const res = await apiFetch<Envelope<{ categories: ExpenseCategory[] }>>(
    `/api/expense-categories${qs ? `?${qs}` : ''}`,
    { headers: authHeaders() },
  );
  return res.data.categories;
}

export interface BulkCategoryInput {
  code: string;
  name: string;
  kind?: CategoryKind;
}

/** Create several categories for one firm (null = shared); known codes are skipped. */
export async function createExpenseCategoriesBulk(
  companyId: string | null,
  categories: BulkCategoryInput[],
): Promise<{ created: ExpenseCategory[]; skipped: string[] }> {
  const res = await apiFetch<Envelope<{ created: ExpenseCategory[]; skipped: string[] }>>(
    '/api/expense-categories/bulk',
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ companyId, categories }),
    },
  );
  return res.data;
}

export async function createExpenseCategory(input: ExpenseCategoryInput): Promise<ExpenseCategory> {
  const res = await apiFetch<Envelope<ExpenseCategory>>('/api/expense-categories', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function updateExpenseCategory(
  id: string,
  input: ExpenseCategoryInput,
): Promise<ExpenseCategory> {
  const res = await apiFetch<Envelope<ExpenseCategory>>(`/api/expense-categories/${id}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}

export async function deleteExpenseCategory(id: string): Promise<void> {
  await apiFetch<Envelope<{ deleted: true }>>(`/api/expense-categories/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
}

// --- Financing codes (not revenue / expense) ---------------------------------

export interface FinancingCodes {
  companyId: string | null;
  codes: string[];
  /** The firm has no list of its own and follows the default one. */
  inherited: boolean;
}

/** Categories whose money is financing (loans, dividends…), per firm (null = default). */
export async function getFinancingCodes(companyId: string | null): Promise<FinancingCodes> {
  const qs = companyId ? `?companyId=${encodeURIComponent(companyId)}` : '';
  const res = await apiFetch<Envelope<FinancingCodes>>(
    `/api/expense-categories/financing-codes${qs}`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function setFinancingCodes(
  companyId: string | null,
  codes: string[],
): Promise<FinancingCodes> {
  const res = await apiFetch<Envelope<FinancingCodes>>('/api/expense-categories/financing-codes', {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify({ companyId, codes }),
  });
  return res.data;
}

/** The firm goes back to following the default list. */
export async function clearFinancingCodes(companyId: string): Promise<FinancingCodes> {
  const res = await apiFetch<Envelope<FinancingCodes>>(
    `/api/expense-categories/financing-codes?companyId=${encodeURIComponent(companyId)}`,
    { method: 'DELETE', headers: authHeaders() },
  );
  return res.data;
}
